import React, { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, Grid } from '@mui/material';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import * as Yup from 'yup';

import { getTrackErrorMessage, useUploadSandboxTrack } from './queries';
import { ControlledCheckboxField } from '../../../components/FormControlsV2/ControlledCheckboxField';
import { ControlledFileField } from '../../../components/FormControlsV2/ControlledFileField';
import { ControlledRadioGroupField } from '../../../components/FormControlsV2/ControlledRadioGroupField';
import { ControlledTextField } from '../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../components/FormControlsV2/FormProvider';
import { ERRORS_TEXTS } from '../../../const/validation';

enum TrackType {
  Digit = 0,
  Percent = 1,
  BrokerReport = 2,
}

const validationSchema = Yup.object({
  fileNames: Yup.array().of(Yup.string()).min(1, ERRORS_TEXTS.required).required(ERRORS_TEXTS.required),
  trackType: Yup.string().required(ERRORS_TEXTS.required),
  sum: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .when('trackType', {
      is: String(TrackType.Digit),
      then: Yup.number().required(ERRORS_TEXTS.required).integer(ERRORS_TEXTS.number).typeError(ERRORS_TEXTS.number),
    }),
});

type FormFields = {
  fileNames: string[];
  trackType: string;
  sum: string | number;
  nkd: boolean;
  isAlgo: boolean;
  [fieldName: string]: unknown;
};

const marketGroups = {
  moexUsd: [{ name: 'ММВБ (USD)', value: 6 }],
  moex: [
    { name: 'Основной рынок ММВБ (акции)', value: 1 },
    { name: 'Основной рынок ММВБ (облигации)', value: 2 },
    { name: 'Цены облигации в %', value: -1 },
    { name: 'Срочный рынок Фортс', value: 4 },
  ],
  spb: [{ name: 'СПБ', value: 3 }],
  us: [{ name: 'US MARKETS', value: 5 }],
  lse: [{ name: 'LSE', value: 7 }],
  spbEur: [{ name: 'СПБ-EUR', value: 8 }],
  spbHkd: [{ name: 'Площадка СПБ-HKD', value: 9 }],
};

const marketIds = Object.values(marketGroups)
  .flat()
  .map(({ value }) => value)
  .filter((value) => value !== -1);

const marketFieldName = (value: number) => `market${value}`;

const getSelectedMarkets = (values: FormFields) => marketIds.filter((value) => Boolean(values[marketFieldName(value)]));

type MarketFieldProps = {
  value: number;
  label: string;
  disabled: boolean;
};

const MarketField: React.FC<MarketFieldProps> = ({ value, label, disabled }) => (
  <ControlledCheckboxField name={marketFieldName(value)} label={label} disabled={disabled} margin="none" />
);

type UploadTrackFormProps = {
  error: string;
  loading: boolean;
  form: UseFormReturn<FormFields>;
  onSubmit: SubmitHandler<FormFields>;
};

const UploadTrackFormView: React.FC<UploadTrackFormProps> = ({ error, loading, form, onSubmit }) => {
  const { control, formState, handleSubmit, setValue, watch } = form;
  const { errors } = formState;
  const values = watch();
  const isBrokerReport = Number(values.trackType) === TrackType.BrokerReport;
  const selectedMarkets = useMemo(() => getSelectedMarkets(values), [values]);
  const isMoexSelected = marketGroups.moex.some(({ value }) => selectedMarkets.includes(value));

  useEffect(() => {
    setValue('sum', 0);
    setValue('isAlgo', Number(values.trackType) === TrackType.Percent);
    setValue('fileNames', []);
  }, [setValue, values.trackType]);

  const isMarketDisabled = useCallback(
    (value: number) => {
      if (selectedMarkets.length === 0) return false;

      if (value === 6) {
        return !selectedMarkets.includes(6) && !isMoexSelected;
      }

      if (value === 1) {
        return !isMoexSelected || selectedMarkets.includes(6);
      }

      if ([2, 4].includes(value)) {
        return !isMoexSelected && !selectedMarkets.includes(6);
      }

      return !selectedMarkets.includes(value);
    },
    [isMoexSelected, selectedMarkets],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormProvider control={control} errors={errors}>
        <ControlledRadioGroupField
          name="trackType"
          label="Тип трека"
          options={[
            { label: 'Доли', value: String(TrackType.Percent) },
            { label: 'Количество', value: String(TrackType.Digit) },
            { label: 'Брокерский отчет', value: String(TrackType.BrokerReport) },
          ]}
          row
        />

        <ControlledFileField
          key={values.trackType}
          name="fileNames"
          fileTypes={isBrokerReport ? ['.xls'] : ['.txt']}
          url="api/sandbox/uploadfile"
          sizeView="kb"
          multiple={isBrokerReport}
          dropHereOr="Прикрепить файлы"
          onRemove={(fileName) => {
            setValue(
              'fileNames',
              values.fileNames.filter((item) => item !== fileName),
              { shouldDirty: true, shouldValidate: true },
            );
          }}
          onUpload={(_fieldName, fileNames) =>
            setValue('fileNames', [fileNames], {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          onUploadMultiple={(_fieldName, fileNames) =>
            setValue('fileNames', fileNames, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />

        {Number(values.trackType) === TrackType.Digit && <ControlledTextField name="sum" label="Стартовая сумма" />}

        {Number(values.trackType) === TrackType.Percent && (
          <ControlledCheckboxField name="isAlgo" label="Стратегия с фиксированными весами" />
        )}

        <Box sx={{ mt: 2 }}>
          <Box sx={{ color: 'text.secondary', mb: 1 }}>Рынки:</Box>
          <Grid container spacing={0.5}>
            {marketGroups.moexUsd.map(({ name, value }) => (
              <Grid item xs={12} sm={6} key={name}>
                <MarketField value={value} label={name} disabled={isMarketDisabled(value)} />
              </Grid>
            ))}

            {marketGroups.moex.map(({ name, value }) => {
              if (value === -1) {
                return selectedMarkets.includes(2) ? (
                  <Grid item xs={12} sm={6} key={name}>
                    <ControlledCheckboxField name="nkd" label={name} margin="none" />
                  </Grid>
                ) : null;
              }

              return (
                <Grid item xs={12} sm={6} key={name}>
                  <MarketField value={value} label={name} disabled={isMarketDisabled(value)} />
                </Grid>
              );
            })}

            {Object.values(marketGroups)
              .filter((group) => group !== marketGroups.moexUsd && group !== marketGroups.moex)
              .flat()
              .map(({ name, value }) => (
                <Grid item xs={12} sm={6} key={name}>
                  <MarketField value={value} label={name} disabled={isMarketDisabled(value)} />
                </Grid>
              ))}
          </Grid>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
          disabled={loading || Object.keys(errors).length > 0}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Загрузить трек
        </Button>
      </FormProvider>
    </form>
  );
};

type Props = {
  strategyId: string;
  onClose: () => void;
};

export const UploadTrackForm: React.FC<Props> = ({ strategyId, onClose }) => {
  const { mutateAsync: uploadTrack, isPending: loading, error: uploadError } = useUploadSandboxTrack();

  const form = useForm<FormFields>({
    defaultValues: {
      fileNames: [],
      trackType: String(TrackType.Percent),
      sum: 0,
      nkd: false,
      isAlgo: true,
      ...Object.fromEntries(marketIds.map((value) => [marketFieldName(value), false])),
    },
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as never,
  });

  const onSubmit = useCallback(
    async (values: FormFields) => {
      try {
        const fileNames = values.fileNames;
        const isBrokerReport = Number(values.trackType) === TrackType.BrokerReport;
        await uploadTrack({
          stratId: strategyId,
          markets: getSelectedMarkets(values),
          calcNkd: values.nkd,
          isAlgo: values.isAlgo,
          type: Number(values.trackType),
          startingFunds: Number(values.sum || 0),
          ...(isBrokerReport ? { fileNames } : { fileName: fileNames[0] }),
        });

        onClose();
      } catch {
        // The mutation error is displayed below through React Query state.
      }
    },
    [onClose, strategyId, uploadTrack],
  );

  const error = uploadError ? getTrackErrorMessage(uploadError) : '';

  return <UploadTrackFormView error={error} loading={loading} form={form} onSubmit={onSubmit} />;
};
